import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon, CheckIcon, SparklesIcon, HomeIcon, CalculatorIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

import { Button, Card, Input, Badge, Spinner } from '@realvisor/ui';
import { useCreateForm, usePipelines, useFormTemplates, type FormConfig, type FormTemplate } from '@/hooks';
import { useProject } from '@/contexts/ProjectContext';
import { ProjectBadge } from '@/components/common/ProjectBadge';

const CATEGORY_LABELS: Record<string, string> = {
  kontakt: 'Kontaktní',
  oceneni: 'Ocenění',
  hypoteka: 'Hypotéka',
  kalkulacka: 'Kalkulačka',
  special: 'Speciální',
};

const CATEGORY_COLORS: Record<string, string> = {
  kontakt: 'bg-blue-100 text-blue-700',
  oceneni: 'bg-green-100 text-green-700',
  hypoteka: 'bg-purple-100 text-purple-700',
  kalkulacka: 'bg-orange-100 text-orange-700',
  special: 'bg-amber-100 text-amber-700',
};

// Varianty Valuo formuláře
const VALUO_VARIANTS = [
  { value: 'complete', label: 'Kompletní', description: 'Prodej i pronájem, všechna pole' },
  { value: 'sale_only', label: 'Pouze prodej', description: 'Bez pronájmu, jen povinná pole' },
  { value: 'lease_only', label: 'Pouze pronájem', description: 'Bez prodeje, jen povinná pole' },
  { value: 'minimal', label: 'Minimální', description: 'Jen základní povinná pole' },
];

// Varianty Hypotéka formuláře (již se nepoužívá - starý formulář odstraněn)

// Speciální vícekrokové formuláře (hardcoded komponenty)
const SPECIAL_TEMPLATES = [
  {
    id: 'valuo',
    slug: 'valuo',
    name: 'Valuo Ocenění',
    description: 'Dvoukrokový formulář pro online ocenění nemovitosti s Mapy.cz našeptávačem a Valuo API',
    category: 'special',
    modules: ['mapy_cz', 'valuo_api'],
    isPremium: true,
    isSpecial: true,
    icon: HomeIcon,
    hasVariants: true,
  },
  {
    id: 'hypokalkulacka',
    slug: 'hypokalkulacka',
    name: 'Hypokalkulačka (Lead)',
    description: 'Hypoteční kalkulačka + Prescoring - Třístupňový formulář. Krok 1: Kalkulačka (okamžitý výsledek), Krok 2: Prescoring (4 otázky), Krok 3: Lead form (email + telefon pro PDF)',
    category: 'special',
    modules: [],
    isPremium: false,
    isSpecial: true,
    icon: CalculatorIcon,
    hasVariants: false,
  },
  {
    id: 'prescoring',
    slug: 'prescoring',
    name: 'Prescoring',
    description: 'Rychlé předběžné posouzení hypotéky ze 4 otázek. Semafor + PDF výstup.',
    category: 'special',
    modules: [],
    isPremium: false,
    isSpecial: true,
    icon: CalculatorIcon,
    hasVariants: false,
  },
];

export default function FormCreate() {
  const navigate = useNavigate();
  const { currentProject } = useProject();
  const { data: pipelines } = usePipelines();
  const { data: templates, isLoading: templatesLoading } = useFormTemplates();
  const createForm = useCreateForm();

  const [step, setStep] = useState<'template' | 'settings'>('template');
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(null);
  const [selectedSpecial, setSelectedSpecial] = useState<typeof SPECIAL_TEMPLATES[0] | null>(null);
  const [selectedVariant, setSelectedVariant] = useState('complete');
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    projectId: '',
    targetPipelineId: '',
    defaultStageId: '',
    isActive: true,
  });

  // Nastavit projectId z aktuálního projektu
  useEffect(() => {
    if (currentProject) {
      setFormData(prev => ({ ...prev, projectId: currentProject.id }));
    }
  }, [currentProject]);

  const selectedPipeline = pipelines?.find(p => p.id === formData.targetPipelineId);

  const generateCode = (name: string) => {
    const baseCode = name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 15);
    
    // Přidat timestamp pro zajištění unikátnosti
    const timestamp = Date.now().toString().slice(-5);
    return `${baseCode}-${timestamp}`;
  };

  const handleSelectTemplate = (template: FormTemplate) => {
    setSelectedTemplate(template);
    setSelectedSpecial(null);
    setFormData({
      ...formData,
      name: template.name,
      code: generateCode(template.name),
    });
    setStep('settings');
  };

  const handleSelectSpecial = (special: typeof SPECIAL_TEMPLATES[0]) => {
    setSelectedSpecial(special);
    setSelectedTemplate(null);
    setFormData({
      ...formData,
      name: special.name,
      code: generateCode(special.name),
    });
    setStep('settings');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.code || !formData.projectId) {
      toast.error('Vyplňte všechna povinná pole');
      return;
    }

    try {
      // Speciální formuláře mají vlastní config
      let config: any;
      if (selectedSpecial) {
        config = {
          formType: selectedSpecial.slug, // 'valuo' nebo 'hypoteka'
          variant: selectedSpecial.hasVariants ? selectedVariant : undefined, // Varianta pro Valuo
          fields: [],
          submitButtonText: selectedSpecial.slug === 'valuo' ? 'Získat odhad ceny' : 'Spočítat hypotéku',
          successMessage: 'Děkujeme!',
        };
      } else {
        config = selectedTemplate?.config || {
          formType: 'contact', // Standardní kontaktní formulář
          fields: [
            { name: 'firstName', label: 'Jméno', type: 'text', required: true },
            { name: 'email', label: 'E-mail', type: 'email', required: true },
            { name: 'phone', label: 'Telefon', type: 'phone', required: false },
            { name: 'website', label: 'Website', type: 'text', required: false, hidden: true, honeypot: true },
          ],
          submitButtonText: 'Odeslat',
          successMessage: 'Děkujeme!',
        };
      }

      const result = await createForm.mutateAsync({
        name: formData.name,
        code: formData.code,
        projectId: formData.projectId,
        config: config as FormConfig,
        targetPipelineId: formData.targetPipelineId || undefined,
        defaultStageId: formData.defaultStageId || undefined,
        isActive: formData.isActive,
      });

      toast.success('Formulář vytvořen');
      // Speciální formuláře nelze editovat v builderu, přesměrovat na detail
      if (selectedSpecial) {
        navigate(`/forms/${result.id}`);
      } else {
        navigate(`/forms/${result.id}/edit`);
      }
    } catch (error: any) {
      toast.error(error.message || 'Nepodařilo se vytvořit formulář');
    }
  };

  // Step 1: Template Selection
  if (step === 'template') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/forms">
            <Button variant="outline" size="sm">
              <ArrowLeftIcon className="h-4 w-4" />
            </Button>
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Nový formulář</h1>
            <p className="text-sm text-gray-500">Vyberte šablonu formuláře</p>
          </div>
        </div>

        {templatesLoading ? (
          <div className="flex justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : (
          <>
            {/* Speciální vícekrokové formuláře */}
            <div className="mb-6">
              <h2 className="text-sm font-medium text-gray-700 mb-3">Vícekrokové formuláře</h2>
              <div className="grid gap-4 sm:grid-cols-2">
                {SPECIAL_TEMPLATES.map((special) => {
                  const Icon = special.icon;
                  return (
                    <button
                      key={special.id}
                      onClick={() => handleSelectSpecial(special)}
                      className="text-left p-4 rounded-xl border-2 border-amber-200 bg-amber-50 hover:border-amber-400 hover:shadow-lg transition-all group"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                          <Icon className="h-5 w-5 text-amber-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="font-semibold text-gray-900 group-hover:text-amber-700 transition-colors">
                              {special.name}
                            </h3>
                            {special.isPremium && (
                              <span className="flex items-center gap-1 text-xs font-medium text-amber-600">
                                <SparklesIcon className="h-3 w-3" />
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 line-clamp-2">
                            {special.description}
                          </p>
                          {special.modules.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {special.modules.map((mod) => (
                                <span key={mod} className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded">
                                  {mod === 'mapy_cz' ? 'Mapy.cz' : mod === 'valuo_api' ? 'Valuo API' : mod}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Standardní šablony */}
            <div>
              <h2 className="text-sm font-medium text-gray-700 mb-3">Standardní šablony</h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                {templates?.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleSelectTemplate(template)}
                    className="text-left p-4 rounded-xl border-2 border-gray-200 hover:border-primary-500 hover:shadow-lg transition-all group"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <span className={`text-xs font-medium px-2 py-1 rounded-full ${CATEGORY_COLORS[template.category] || 'bg-gray-100 text-gray-700'}`}>
                        {CATEGORY_LABELS[template.category] || template.category}
                      </span>
                      {template.isPremium && (
                        <span className="flex items-center gap-1 text-xs font-medium text-amber-600">
                          <SparklesIcon className="h-3 w-3" />
                          Premium
                        </span>
                      )}
                    </div>
                    <h3 className="font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                      {template.name}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                      {template.description}
                    </p>
                    {template.modules.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {template.modules.map((mod) => (
                          <span key={mod} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded">
                            {mod === 'mapy_cz' ? 'Mapy.cz' : mod === 'valuo_api' ? 'Valuo API' : mod}
                          </span>
                        ))}
                      </div>
                    )}
                  </button>
                ))}

                {/* Custom form option */}
                <button
                  onClick={() => {
                    setSelectedTemplate(null);
                    setSelectedSpecial(null);
                    setStep('settings');
                  }}
                  className="text-left p-4 rounded-xl border-2 border-dashed border-gray-300 hover:border-primary-500 hover:bg-primary-50 transition-all"
                >
                  <div className="text-center py-4">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gray-100 flex items-center justify-center">
                      <span className="text-2xl text-gray-400">+</span>
                    </div>
                    <h3 className="font-semibold text-gray-900">Vlastní formulář</h3>
                    <p className="text-sm text-gray-500 mt-1">Vytvořte formulář od začátku</p>
                  </div>
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Step 2: Settings
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="outline" size="sm" onClick={() => setStep('template')}>
          <ArrowLeftIcon className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {selectedSpecial ? selectedSpecial.name : selectedTemplate ? selectedTemplate.name : 'Vlastní formulář'}
          </h1>
          <p className="text-sm text-gray-500">Nastavte základní údaje</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Základní informace</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Název *</label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value, code: formData.code || generateCode(e.target.value) })}
                  placeholder="Kontaktní formulář"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Kód *</label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                  placeholder="kontaktni-formular"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">Unikátní identifikátor pro embed</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Projekt</label>
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                  {currentProject ? (
                    <ProjectBadge projectId={currentProject.id} size="sm" showName />
                  ) : (
                    <span className="text-sm text-gray-500">Není vybrán projekt</span>
                  )}
                </div>
                {!currentProject && (
                  <p className="mt-1 text-xs text-red-500">Pro vytvoření formuláře musíte mít vybraný projekt</p>
                )}
              </div>
            </div>
          </Card>

          <Card padding="md">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Pipeline</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cílová pipeline</label>
                <select
                  value={formData.targetPipelineId}
                  onChange={(e) => setFormData({ ...formData, targetPipelineId: e.target.value, defaultStageId: '' })}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  <option value="">Výchozí</option>
                  {pipelines?.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>
              {selectedPipeline && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Výchozí fáze</label>
                  <select
                    value={formData.defaultStageId}
                    onChange={(e) => setFormData({ ...formData, defaultStageId: e.target.value })}
                    className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="">První fáze</option>
                    {selectedPipeline.stages?.map((s: any) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Template info */}
        {selectedTemplate && (
          <Card padding="md" className="mt-6">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <CheckIcon className="h-5 w-5 text-primary-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">Šablona: {selectedTemplate.name}</h3>
                <p className="text-sm text-gray-500 mt-1">{selectedTemplate.description}</p>
                {selectedTemplate.modules.length > 0 && (
                  <div className="flex gap-2 mt-2">
                    {selectedTemplate.modules.map((mod) => (
                      <Badge key={mod} variant="default">
                        {mod === 'mapy_cz' ? '📍 Mapy.cz' : mod === 'valuo_api' ? '🏠 Valuo API' : mod}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </Card>
        )}

        {/* Special template info + variant selection */}
        {selectedSpecial && (
          <Card padding="md" className="mt-6 border-amber-200 bg-amber-50">
            <div className="flex items-start gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                {selectedSpecial.icon && <selectedSpecial.icon className="h-5 w-5 text-amber-600" />}
              </div>
              <div className="flex-1">
                <h3 className="font-medium text-gray-900">Speciální formulář: {selectedSpecial.name}</h3>
                <p className="text-sm text-gray-600 mt-1">{selectedSpecial.description}</p>
                {selectedSpecial.modules.length > 0 && (
                  <div className="flex gap-2 mt-2">
                    {selectedSpecial.modules.map((mod) => (
                      <Badge key={mod} variant="default">
                        {mod === 'mapy_cz' ? '📍 Mapy.cz' : mod === 'valuo_api' ? '🏠 Valuo API' : mod}
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* Výběr varianty - pouze pro Valuo */}
                {selectedSpecial.hasVariants && selectedSpecial.slug === 'valuo' && (
                  <div className="mt-4 pt-4 border-t border-amber-200">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Varianta formuláře
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {VALUO_VARIANTS.map((variant) => (
                        <button
                          key={variant.value}
                          type="button"
                          onClick={() => setSelectedVariant(variant.value)}
                          className={`p-3 rounded-lg border-2 text-left transition-all ${
                            selectedVariant === variant.value
                              ? 'border-amber-500 bg-amber-100'
                              : 'border-gray-200 bg-white hover:border-amber-300'
                          }`}
                        >
                          <div className="font-medium text-sm text-gray-900">{variant.label}</div>
                          <div className="text-xs text-gray-500 mt-0.5">{variant.description}</div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                
                <p className="text-xs text-amber-700 mt-3">
                  ⚠️ Tento formulář má vlastní vícekrokové rozhraní a nelze ho upravovat v editoru.
                </p>
              </div>
            </div>
          </Card>
        )}

        <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline" onClick={() => setStep('template')}>Zpět</Button>
          <Button type="submit" disabled={createForm.isPending || !currentProject}>
            {createForm.isPending ? 'Vytvářím...' : 'Vytvořit formulář'}
          </Button>
        </div>
      </form>
    </div>
  );
}
